import React, { useState } from "react";
import {
  Box, Typography, TextField, FormControlLabel, Switch, Button, Alert, Divider,
} from "@mui/material";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { NAVY, TEXT_SECONDARY } from "../../../theme/tokens";
import {
  MapForm, buildMapEmbedUrl, parseMapEmbedUrl,
  DEFAULT_MAP_ZOOM, MIN_MAP_ZOOM, MAX_MAP_ZOOM, isGoogleMapsEmbedUrl,
} from "./mapConfig";
import { isValidWebUrl } from "./websiteConfig";

/**
 * Map zone inspector. A user-friendly location workflow that hides all iframe
 * complexity: the operator types an address (or coordinates) and the widget
 * renders a keyless Google Maps embed. Advanced users can paste a ready-made
 * map embed URL instead. Stored config is a plain website-family config
 * (`url` + `display_mode: "iframe_map"`), so it renders in the editor, preview
 * and on the device with no schema change.
 */
interface Props {
  cfg: Record<string, any>;
  setCfg: (patch: Record<string, any>) => void;
  interacting: boolean;
  onToggleInteract: () => void;
}

const MapInspector: React.FC<Props> = ({ cfg, setCfg, interacting, onToggleInteract }) => {
  // Seed the form from the stored URL so re-opening a saved map repopulates.
  const [form, setForm] = useState<MapForm>(() => parseMapEmbedUrl(cfg.url));
  const [advanced, setAdvanced] = useState<boolean>(() =>
    !!cfg.url && !isGoogleMapsEmbedUrl(cfg.url));

  // Any location-field change rebuilds the embed URL in a single patch. We
  // always pin display_mode so the renderer treats it as a map.
  const commitForm = (next: MapForm) => {
    setForm(next);
    setCfg({ url: buildMapEmbedUrl(next), display_mode: "iframe_map" });
  };
  const patch = (p: Partial<MapForm>) => commitForm({ ...form, ...p });

  const hasLocation = !!buildMapEmbedUrl(form);

  return (
    <Box>
      {!advanced ? (
        <>
          <TextField
            size="small" fullWidth label="Location / Address"
            placeholder="e.g. Oshawa, Ontario, Canada"
            value={form.query}
            onChange={(e) => patch({ query: e.target.value })}
            helperText="Type a place or address. Coordinates below override this."
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.5 }}>
            <TextField
              size="small" label="Latitude" type="number" placeholder="optional"
              value={form.lat}
              onChange={(e) => patch({ lat: e.target.value })}
            />
            <TextField
              size="small" label="Longitude" type="number" placeholder="optional"
              value={form.lng}
              onChange={(e) => patch({ lng: e.target.value })}
            />
          </Box>
          <TextField
            size="small" fullWidth type="number" label="Zoom"
            inputProps={{ min: MIN_MAP_ZOOM, max: MAX_MAP_ZOOM, step: 1 }}
            value={form.zoom}
            onChange={(e) => {
              const z = Number(e.target.value);
              patch({ zoom: Number.isFinite(z) ? z : DEFAULT_MAP_ZOOM });
            }}
            helperText={`${MIN_MAP_ZOOM} (world) – ${MAX_MAP_ZOOM} (building)`}
            sx={{ mb: 0.5 }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={form.marker}
              onChange={(e) => patch({ marker: e.target.checked })} />}
            label={<Typography sx={{ fontSize: 13 }}>Show marker</Typography>}
            sx={{ display: "flex", ml: 0 }}
          />
          {!form.marker && (
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 0.5 }}>
              Hiding the marker needs explicit latitude &amp; longitude.
            </Typography>
          )}
          {!hasLocation && (
            <Alert severity="info" sx={{ mb: 1, fontSize: 12, py: 0.5 }}>
              Enter an address or coordinates to show the map.
            </Alert>
          )}
        </>
      ) : (
        <TextField
          size="small" fullWidth label="Map embed URL"
          placeholder="https://www.google.com/maps?...&output=embed"
          value={cfg.url ?? ""}
          onChange={(e) => {
            const url = e.target.value.trim();
            setCfg({ url, display_mode: "iframe_map" });
            setForm(parseMapEmbedUrl(url));
          }}
          error={!!cfg.url && !isValidWebUrl(cfg.url)}
          helperText={
            cfg.url && !isValidWebUrl(cfg.url)
              ? "Enter a valid https:// map embed URL."
              : "Paste a Google/other map embed URL (the src of the map iframe)."
          }
          sx={{ mb: 1.5 }}
        />
      )}

      <Button size="small" onClick={() => setAdvanced((a) => !a)}
        sx={{ textTransform: "none", fontSize: 12, px: 0, mb: 1 }}>
        {advanced ? "← Use address search" : "Advanced: paste a map embed URL"}
      </Button>

      <Divider sx={{ my: 1 }} />

      <Typography sx={{ fontSize: 11, fontWeight: 700, color: NAVY, mb: 0.5,
        textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Behaviour
      </Typography>
      <FormControlLabel
        control={<Switch size="small" checked={!!cfg.allow_interaction}
          onChange={(e) => setCfg({ allow_interaction: e.target.checked })} />}
        label={<Typography sx={{ fontSize: 13 }}>Allow interaction (pan / zoom)</Typography>}
        sx={{ display: "flex", ml: 0 }}
      />
      <FormControlLabel
        control={<Switch size="small" checked={!!cfg.allow_fullscreen}
          onChange={(e) => setCfg({ allow_fullscreen: e.target.checked })} />}
        label={<Typography sx={{ fontSize: 13 }}>Allow fullscreen</Typography>}
        sx={{ display: "flex", ml: 0 }}
      />

      <Button
        fullWidth size="small" variant={interacting ? "contained" : "outlined"}
        color={interacting ? "secondary" : "primary"}
        startIcon={<TouchAppIcon />}
        onClick={onToggleInteract}
        sx={{ mt: 1.5 }}
      >
        {interacting ? "Return to editing" : "Interact with map"}
      </Button>
      {interacting && (
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.75 }}>
          The map is now pannable/zoomable on the canvas. Dragging and resizing
          are paused — choose “Return to editing” to move or resize the zone again.
        </Typography>
      )}
    </Box>
  );
};

export default MapInspector;
